import axios from './axiosConfig';

// Use backend proxy to avoid CORS issues
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5005';
const GEOCODING_BASE_URL = `${API_URL}/api/v1/geocoding`;

/**
 * Convert address to coordinates using Nominatim geocoding (via backend proxy)
 * @param {string} address - The address to geocode
 * @returns {Promise<Object>} - Object with lat, lng, and display_name
 */
export const geocodeAddress = async (address) => {
  try {
    const response = await axios.get(`${GEOCODING_BASE_URL}/search`, {
      params: {
        q: address,
        limit: 5, // Return top 5 results
      },
    });

    if (response.data.success && response.data.data && response.data.data.length > 0) {
      return response.data.data.map(result => ({
        lat: parseFloat(result.lat),
        lng: parseFloat(result.lon),
        display_name: result.display_name,
        type: result.type,
        importance: result.importance,
      }));
    } else {
      throw new Error('No results found for this address');
    }
  } catch (error) {
    console.error('Geocoding error:', error);
    throw error;
  }
};

/**
 * Convert coordinates to address using reverse geocoding (via backend proxy)
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @returns {Promise<Object>} - Object with address information
 */
export const reverseGeocode = async (lat, lng) => {
  try {
    const response = await axios.get(`${GEOCODING_BASE_URL}/reverse`, {
      params: {
        lat: lat,
        lon: lng,
      },
    });

    if (response.data.success && response.data.data) {
      return {
        display_name: response.data.data.display_name,
        address: response.data.data.address,
      };
    } else {
      throw new Error('No address found for these coordinates');
    }
  } catch (error) {
    console.error('Reverse geocoding error:', error);
    throw error;
  }
};

/**
 * Parse coordinate string in various formats
 * @param {string} coordString - Coordinate string (e.g., "34.5, 69.2" or "34.5,69.2")
 * @returns {Object|null} - Object with lat and lng, or null if invalid
 */
export const parseCoordinates = (coordString) => {
  if (!coordString || typeof coordString !== 'string') {
    return null;
  }

  // Remove extra spaces and split by comma
  const parts = coordString.trim().split(/[,\s]+/);

  if (parts.length !== 2) {
    return null;
  }

  const lat = parseFloat(parts[0]);
  const lng = parseFloat(parts[1]);

  // Validate coordinates
  if (isNaN(lat) || isNaN(lng)) {
    return null;
  }

  // Check valid ranges
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
    return null;
  }

  return { lat, lng };
};

/**
 * Validate if a string is a valid coordinate format
 * @param {string} coordString - String to validate
 * @returns {boolean} - True if valid coordinate format
 */
export const isValidCoordinateString = (coordString) => {
  return parseCoordinates(coordString) !== null;
};

/**
 * Search for addresses with autocomplete suggestions (via backend proxy)
 * @param {string} query - Search query
 * @returns {Promise<Array>} - Array of search suggestions
 */
export const searchAddresses = async (query) => {
  if (!query || query.length < 3) {
    return [];
  }

  try {
    const response = await axios.get(`${GEOCODING_BASE_URL}/search`, {
      params: {
        q: query,
        limit: 5,
      },
    });

    if (response.data.success && response.data.data && response.data.data.length > 0) {
      return response.data.data.map(result => ({
        display_name: result.display_name,
        lat: parseFloat(result.lat),
        lng: parseFloat(result.lon),
        type: result.type,
      }));
    }

    return [];
  } catch (error) {
    console.error('Address search error:', error);
    return [];
  }
};
